import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuthRefresh } from '../lib/useAuthRefresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ExpenseContext = createContext(null);

export const useExpense = () => {
  const context = useContext(ExpenseContext);
  if (!context) {
    throw new Error('useExpense must be used within an ExpenseProvider');
  }
  return context;
};

export const ExpenseProvider = ({ children, token }) => {
  // Silent access-token refresh — see useAuthRefresh for the rationale.
  useAuthRefresh(token, { backendUrl: BACKEND_URL, apiPrefix: '/api/expenses' });

  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total: 0,
    total_pages: 0
  });
  const [lov, setLov] = useState({
    categories: [],
    payment_modes: [],
    payers: []
  });
  const [filters, setFilters] = useState({
    year: null,
    quarter: null,
    month: null,
    category: null,
    search: ''
  });

  // isReady is true when we have a token
  const isReady = Boolean(token);

  // Fetch LOV data - use direct axios call
  const fetchLOV = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/expenses/settings/lov`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      setLov({
        categories: response.data.categories?.map(i => i.value) || [],
        payment_modes: response.data.payment_modes?.map(i => i.value) || [],
        payers: response.data.payers?.map(i => i.value) || []
      });
    } catch (err) {
      console.error('Failed to fetch LOV:', err);
    }
  }, [token]);

  // Fetch expenses with pagination
  const fetchExpenses = useCallback(async (customFilters = {}, page = 1, pageSize = 50) => {
    setLoading(true);
    try {
      const params = { ...filters, ...customFilters };
      const queryParams = new URLSearchParams();
      
      // Pagination
      queryParams.append('page', page);
      queryParams.append('page_size', pageSize);
      
      if (params.year) queryParams.append('year', params.year);
      if (params.quarter) queryParams.append('quarter', params.quarter);
      if (params.month) queryParams.append('month', params.month);
      if (params.category) queryParams.append('category', params.category);
      if (params.search) queryParams.append('search', params.search);
      if (params.date) queryParams.append('date', params.date);
      // Support for date range filtering
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      
      // Construct full URL to ensure HTTPS is preserved
      const fullUrl = `${BACKEND_URL}/api/expenses?${queryParams.toString()}`;
      
      // Use axios directly with full URL instead of instance
      const response = await axios.get(fullUrl, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      
      // Handle new paginated response format
      if (response.data.data && response.data.pagination) {
        setExpenses(response.data.data);
        setPagination(response.data.pagination);
        return response.data;
      } else {
        // Fallback for old format (array response)
        setExpenses(response.data);
        return { data: response.data, pagination: { page: 1, page_size: response.data.length, total: response.data.length, total_pages: 1 } };
      }
    } catch (err) {
      console.error('Failed to fetch expenses:', err);
      return { data: [], pagination: { page: 1, page_size: 50, total: 0, total_pages: 0 } };
    } finally {
      setLoading(false);
    }
  }, [filters, token]);

  // Create expense
  // Create expense - use direct axios call with full URL to preserve HTTPS
  const createExpense = useCallback(async (expenseData) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/expenses`, expenseData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      await fetchExpenses();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to create expense' };
    }
  }, [fetchExpenses, token]);

  // Update expense - use direct axios call with full URL to preserve HTTPS
  const updateExpense = useCallback(async (id, expenseData) => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/expenses/${id}`, expenseData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      await fetchExpenses();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to update expense' };
    }
  }, [fetchExpenses, token]);

  // Delete expense - use direct axios call with full URL to preserve HTTPS
  const deleteExpense = useCallback(async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/expenses/${id}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      await fetchExpenses();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to delete expense' };
    }
  }, [fetchExpenses, token]);

  // Duplicate expense - use direct axios call
  const duplicateExpense = useCallback(async (id) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/expenses/${id}/duplicate`, {}, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      await fetchExpenses();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to duplicate expense' };
    }
  }, [fetchExpenses, token]);

  // Bulk delete - use direct axios call
  const bulkDelete = useCallback(async (startDate, endDate, deleteAll = false) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/expenses/bulk/delete`, {
        start_date: startDate,
        end_date: endDate,
        delete_all: deleteAll
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      await fetchExpenses();
      return { success: true, message: response.data.message };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to bulk delete' };
    }
  }, [fetchExpenses, token]);

  // Import expenses - use direct axios call
  const importExpenses = useCallback(async (records) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/expenses/import-records`, 
        { records },
        {
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : ''
          }
        }
      );
      // Refresh list separately — don't let a fetch failure mask the successful import
      try {
        await fetchExpenses();
      } catch (_) {
        // Import succeeded, list refresh failed — ignore
      }
      return { success: true, data: response.data };
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      let errorMsg = 'Failed to import';
      if (detail) {
        errorMsg = detail;
      } else if (status === 401) {
        errorMsg = 'Authentication failed. Please re-open the app from the dashboard.';
      } else if (!err.response) {
        errorMsg = 'Network error — could not reach server. Please try again.';
      }
      return { success: false, error: errorMsg };
    }
  }, [fetchExpenses, token]);

  // Export expenses - use direct axios call
  const exportExpenses = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.year) params.append('year', filters.year);
      if (filters.category) params.append('category', filters.category);
      
      const response = await axios.get(`${BACKEND_URL}/api/expenses/export/xlsx?${params.toString()}`, {
        responseType: 'blob',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `expenses_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Failed to export' };
    }
  }, [filters, token]);

  // Dashboard KPIs - use direct axios call
  const getDashboardKPIs = useCallback(async (year, quarter = null, month = null) => {
    try {
      const params = new URLSearchParams({ year });
      if (quarter) params.append('quarter', quarter);
      if (month) params.append('month', month);
      
      const response = await axios.get(`${BACKEND_URL}/api/expenses/dashboard/kpis?${params.toString()}`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch KPIs:', err);
      return null;
    }
  }, [token]);

  // Monthly trend - use direct axios call
  const getMonthlyTrend = useCallback(async (year) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/expenses/dashboard/monthly-trend?year=${year}`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch trend:', err);
      return [];
    }
  }, [token]);

  // Category breakdown - use direct axios call
  const getCategoryBreakdown = useCallback(async (year, quarter = null, month = null) => {
    try {
      const params = new URLSearchParams({ year });
      if (quarter) params.append('quarter', quarter);
      if (month) params.append('month', month);
      
      const response = await axios.get(`${BACKEND_URL}/api/expenses/dashboard/category-breakdown?${params.toString()}`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch breakdown:', err);
      return [];
    }
  }, [token]);

  // Top expenses - use direct axios call
  const getTopExpenses = useCallback(async (year, quarter = null, month = null, limit = 5) => {
    try {
      const params = new URLSearchParams({ year, limit });
      if (quarter) params.append('quarter', quarter);
      if (month) params.append('month', month);
      
      const response = await axios.get(`${BACKEND_URL}/api/expenses/dashboard/top-expenses?${params.toString()}`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch top expenses:', err);
      return [];
    }
  }, [token]);

  // Category details (drill-down) - use direct axios call
  const getCategoryDetails = useCallback(async (category, year, quarter = null, month = null) => {
    try {
      const params = new URLSearchParams({ year });
      if (quarter) params.append('quarter', quarter);
      if (month) params.append('month', month);
      
      const response = await axios.get(`${BACKEND_URL}/api/expenses/dashboard/category-details/${encodeURIComponent(category)}?${params.toString()}`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch category details:', err);
      return [];
    }
  }, [token]);

  // Reports - use direct axios call
  const getPeriodComparison = useCallback(async (year, periodType, categories = null) => {
    try {
      const params = new URLSearchParams({ year, period_type: periodType });
      if (categories) params.append('categories', categories);
      
      const response = await axios.get(`${BACKEND_URL}/api/expenses/reports/period-comparison?${params.toString()}`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch report:', err);
      return [];
    }
  }, [token]);

  // Audit logs with pagination - use direct axios call
  const getAuditLogs = useCallback(async (page = 1, pageSize = 50, action = null) => {
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (action) params.append('action', action);
      
      const response = await axios.get(`${BACKEND_URL}/api/expenses/audit-logs?${params.toString()}`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return response.data;
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
      return { data: [], pagination: { page: 1, page_size: 50, total: 0, total_pages: 0 } };
    }
  }, [token]);

  // Settings - use direct axios calls
  const addLOVItem = useCallback(async (lovType, value) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/expenses/settings/lov/${lovType}/add?value=${encodeURIComponent(value)}`, {}, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      await fetchLOV();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to add item' };
    }
  }, [fetchLOV, token]);

  const removeLOVItem = useCallback(async (lovType, itemId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/expenses/settings/lov/${lovType}/${itemId}`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      await fetchLOV();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to remove item' };
    }
  }, [fetchLOV, token]);

  const getLOVRaw = useCallback(async (lovType) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/expenses/settings/lov/${lovType}`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return response.data;
    } catch (err) {
      return { type: lovType, items: [] };
    }
  }, [token]);

  // Backup - use direct axios call
  const createBackup = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/expenses/settings/backup`, {
        responseType: 'blob',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `harmony_expense_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      return { success: true };
    } catch (err) {
      const errorMessage = err.response?.status === 403 
        ? 'Only Super Admin can download backups' 
        : err.response?.data?.detail || 'Failed to download backup. Please try again.';
      return { success: false, error: errorMessage };
    }
  }, [token]);

  // Restore - use direct axios call
  const restoreBackup = useCallback(async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${BACKEND_URL}/api/expenses/settings/restore`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      await fetchExpenses();
      await fetchLOV();
      return { success: true, data: response.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.detail || 'Failed to restore' };
    }
  }, [fetchExpenses, fetchLOV, token]);

  const value = {
    expenses,
    loading,
    pagination,
    lov,
    filters,
    isReady,
    setFilters,
    fetchExpenses,
    fetchLOV,
    createExpense,
    updateExpense,
    deleteExpense,
    duplicateExpense,
    bulkDelete,
    importExpenses,
    exportExpenses,
    getDashboardKPIs,
    getMonthlyTrend,
    getCategoryBreakdown,
    getTopExpenses,
    getCategoryDetails,
    getPeriodComparison,
    getAuditLogs,
    addLOVItem,
    removeLOVItem,
    getLOVRaw,
    createBackup,
    restoreBackup
  };

  return (
    <ExpenseContext.Provider value={value}>
      {children}
    </ExpenseContext.Provider>
  );
};

export default ExpenseContext;
